<template>
  <section class="home content">
    <div class="home-quick-entry">
      <button
        v-for="entry in visibleQuickEntries"
        :key="entry.item || entry.url"
        class="quick-entry"
        type="button"
        @click="openEntry(entry.url || entry.item)"
      >
        <img v-if="entry.icon" :src="`/static/img/${entry.icon}`" alt="" />
        <span :title="entry.name">{{ entry.name }}</span>
      </button>
      <button v-if="canCustomize" class="customize-entry" type="button" @click="openCustomize">
        <el-icon><Plus /></el-icon>
        <span>自定义</span>
      </button>
    </div>

    <div class="home-summary">
      <article class="user-card">
        <img class="summary-background" src="/static/img/index-one.png" alt="" />
        <div class="user-title">
          <strong>{{ auth.user?.userName || '用户' }}，欢迎您</strong>
          <button type="button" @click="openManual">用户手册</button>
        </div>
        <p>所属医院：{{ auth.user?.orgName || '-' }}</p>
        <ApiImage v-if="userPicture" class="hospital-picture" :src="userPicture" alt="" />
      </article>
      <button class="summary-card patient" type="button" @click="goTo('/patient')">
        <img class="summary-background" src="/static/img/index-two.png" alt="" />
        <span>患者信息</span>
        <strong>{{ counts.patientCount ?? '-' }}</strong>
        <small>男性{{ counts.manCount ?? 0 }} 女性{{ counts.womanCount ?? 0 }} 未知{{ counts.hunmanCount ?? 0 }}</small>
      </button>
      <button class="summary-card ivus" type="button" @click="goTo('/ivusmanage')">
        <img class="summary-background" src="/static/img/index-three.png" alt="" />
        <span>IVUS影像</span>
        <strong>{{ counts.ivusCount ?? '-' }}</strong>
        <small>本月上传{{ counts.nowIvusCount ?? 0 }}条数据</small>
      </button>
      <button v-if="auth.version === 'clinic'" class="summary-card clinic" type="button" @click="goTo('/clinic')">
        <img class="summary-background" src="/static/img/index-four.png" alt="" />
        <span>临床项目</span>
        <strong>{{ counts.clinicalCount ?? '-' }}</strong>
        <small>共{{ counts.clinicalCount ?? 0 }}个临床项目</small>
      </button>
    </div>

    <section class="home-panel">
      <el-tabs v-model="activeRecentTab" class="recent-tabs">
        <el-tab-pane name="recent" label="最近七天上传的IVUS影像" class="recent-tab-pane">
          <div class="panel-search">
            <el-input
              v-model.trim="searchValue"
              class="home-search"
              placeholder="搜索患者名称或病案号"
              @keyup.enter="loadRecentIvus"
            >
              <template #append>
                <el-button aria-label="搜索" @click="loadRecentIvus"
                  ><el-icon><Search /></el-icon
                ></el-button>
              </template>
            </el-input>
          </div>
          <div class="table-area">
            <el-table :data="recentIvus" class="recent-table" height="100%" empty-text="暂无数据">
              <el-table-column prop="mNo" label="病案号" min-width="140" />
              <el-table-column label="患者姓名" min-width="140"
                ><template #default="scope">{{
                  scope.row.displayName || scope.row.name || '-'
                }}</template></el-table-column
              >
              <el-table-column label="RUN" min-width="90">
                <template #default="scope">
                  <el-button
                    v-if="Number(scope.row.runCount) > 0"
                    link
                    type="primary"
                    @click="openIvusDetail(scope.row)"
                    >{{ scope.row.runCount }}</el-button
                  >
                  <span v-else class="empty-run">0</span>
                </template>
              </el-table-column>
              <el-table-column prop="admissionTime" label="入院时间" min-width="150" />
              <el-table-column prop="uploadName" label="上传人" min-width="120" />
              <el-table-column prop="createTime" label="上传时间" min-width="170" />
              <el-table-column label="操作" width="100"
                ><template #default="scope"
                  ><el-button link type="primary" @click="openIvusDetail(scope.row)">详情</el-button></template
                ></el-table-column
              >
            </el-table>
          </div>
          <div class="panel-action"><el-button type="primary" @click="goTo('/ivusmanage')">进入IVUS管理</el-button></div>
        </el-tab-pane>
        <el-tab-pane name="storage" label="存储空间使用情况" class="storage-tab-pane">
          <div class="storage-space-content">
            <VChart class="storage-chart" :option="storageChartOption" autoresize :aria-label="storageAriaLabel" />
            <div class="storage-space-info">
              <div v-if="!storageSpaceSummary.hasData" class="storage-empty">暂无存储空间数据</div>
              <div v-else class="storage-summary-list">
                <div v-for="segment in storageInfoSegments" :key="segment.key" class="storage-summary-item">
                  <span class="storage-summary-marker" :style="{ backgroundColor: segment.color }" />
                  <div class="storage-summary-text">
                    <strong>{{ segment.label }}</strong>
                    <span>{{ segment.percent }}% · {{ formatStorageSize(segment.size) }}</span>
                  </div>
                </div>
              </div>
              <div v-if="storageSpaceSummary.total !== undefined" class="storage-total">
                总空间：{{ formatStorageSize(storageSpaceSummary.total) }}
              </div>
              <div v-if="storagePath" class="storage-path">存储路径：{{ storagePath }}</div>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </section>

    <el-dialog
      v-model="customizeVisible"
      class="quick-entry-dialog"
      title="自定义常用操作链接"
      width="480px"
      :close-on-click-modal="false"
      @closed="resetCustomize"
    >
      <div class="quick-editor">
        <div class="quick-editor-row quick-editor-header">
          <span>排序</span>
          <span>图标</span>
          <span>操作项目</span>
          <span>是否显示</span>
        </div>
        <div v-for="(entry, index) in quickEntryDraft" :key="entry.id || index" class="quick-editor-row">
          <div class="quick-order">{{ index + 1 }}</div>
          <el-select
            v-model="entry.icon"
            class="quick-icon-select"
            placeholder="请选择"
            clearable
            :disabled="index < 2"
            size="large"
          >
            <template #label="{ value }">
              <img v-if="value" class="selected-quick-icon" :src="`/static/img/${value}`" alt="" />
            </template>
            <el-option v-for="option in iconOptions" :key="option.value" :label="option.label" :value="option.value">
              <img class="quick-icon-option" :src="`/static/img/${option.value}`" :alt="option.label" />
            </el-option>
          </el-select>
          <el-select v-model="entry.item" size="large" placeholder="请选择操作项目" clearable :disabled="index < 2">
            <el-option
              v-for="option in projectOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
          <el-select v-model="entry.status" size="large" placeholder="请选择是否显示" clearable :disabled="index < 2">
            <el-option label="显示" value="1" />
            <el-option label="不显示" value="0" />
          </el-select>
        </div>
      </div>
      <template #footer>
        <el-button type="primary" size="large" :loading="customizeSaving" @click="saveCustomize">保存</el-button>
        <el-button size="large" :disabled="customizeSaving" @click="customizeVisible = false">取消</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { Plus, Search } from '@element-plus/icons-vue'
import { use } from 'echarts/core'
import { TooltipComponent } from 'echarts/components'
import { PieChart } from 'echarts/charts'
import type { EChartsOption } from 'echarts'
import { LabelLayout } from 'echarts/features'
import { CanvasRenderer } from 'echarts/renderers'
import { useRouter } from 'vue-router'
import VChart from 'vue-echarts'
import ApiImage from '@/components/ui/ApiImage.vue'
import {
  getDiskSpace,
  getHomeCounts,
  getQuickEntries,
  getQuickEntryOptions,
  getRecentIvus,
  updateQuickEntries,
  type DiskSpace,
  type HomeCounts,
  type QuickEntry,
  type QuickEntryOption,
  type RecentIvus
} from '@/services/home/home'
import { useAuthStore } from '@/stores/auth'
import {
  buildQuickEntryPayload,
  createQuickEntryDraft,
  getQuickEntryName,
  QuickEntryValidationError,
  resolveQuickEntryTarget
} from './quick-entry'

use([PieChart, TooltipComponent, LabelLayout, CanvasRenderer])

const auth = useAuthStore()
const router = useRouter()
const counts = reactive<HomeCounts>({})
const quickEntries = ref<QuickEntry[]>([])
const quickEntryDraft = ref<QuickEntry[]>([])
const iconOptions = ref<QuickEntryOption[]>([])
const projectOptions = ref<QuickEntryOption[]>([])
const recentIvus = ref<RecentIvus[]>([])
const diskSpace = ref<DiskSpace>({})
const searchValue = ref('')
const activeRecentTab = ref('recent')
const customizeVisible = ref(false)
const customizeSaving = ref(false)
const visibleQuickEntries = computed(() =>
  quickEntries.value
    .filter((entry) => entry.status === '1')
    .map((entry) => ({ ...entry, name: getQuickEntryName(entry, projectOptions.value) }))
)
const canCustomize = computed(() =>
  (auth.user?.permissions || []).some((permission) => permission === '*:*:*' || permission === 'index:menu:set')
)
const userPicture = computed(() => {
  return auth.user?.picturePath?.trim() || auth.user?.organization?.picturePath?.trim() || ''
})

const storageSpaceSummary = computed(() => {
  let total = firstStorageValue(diskSpace.value, [
    'totalSpace',
    'total',
    'capacity',
    'totalSize',
    'diskTotalSpace',
    'diskTotal',
    'diskSpace'
  ])
  let used = firstStorageValue(diskSpace.value, [
    'usedSpace',
    'used',
    'occupySpace',
    'useSpace',
    'usedSize',
    'diskUsedSpace',
    'usedDiskSpace',
    'diskSpace'
  ])
  let free = firstStorageValue(diskSpace.value, [
    'freeSpace',
    'free',
    'availableSpace',
    'available',
    'usableSpace',
    'surplusSpace',
    'remain',
    'remainSpace',
    'remainingSpace',
    'freeSize',
    'diskFreeSpace',
    'freeDiskSpace'
  ])
  const usage = firstStoragePercentage(diskSpace.value, [
    'usedPercent',
    'usageRate',
    'useRate',
    'usePercent',
    'percent'
  ])

  if (total === undefined && used !== undefined && free !== undefined) total = used + free
  if (total === undefined && used !== undefined) total = used
  if (total === undefined && free !== undefined) total = free
  if (usage !== undefined) {
    if (total !== undefined) {
      used = total * usage
      free = total - used
    } else {
      used = usage
      free = 1 - usage
      total = 1
    }
  }
  if (total !== undefined) {
    if (used === undefined && free !== undefined) used = Math.max(total - free, 0)
    if (free === undefined && used !== undefined) free = Math.max(total - used, 0)
  }
  if (total === undefined || total <= 0 || (used === undefined && free === undefined)) {
    return { total: undefined, used: undefined, free: undefined, usedPercent: 0, hasData: false }
  }

  used = Math.max(used || 0, 0)
  free = Math.max(free || 0, 0)
  total = Math.max(total, used + free)
  const usedPercent = Math.round(Math.min(used / total, 1) * 1000) / 10
  return { total, used, free, usedPercent, hasData: true }
})
const storageSegments = computed(() => {
  const summary = storageSpaceSummary.value
  if (!summary.hasData) return []
  const freePercent = Math.round((100 - summary.usedPercent) * 10) / 10
  return [
    { key: 'used', label: '已使用', percent: summary.usedPercent, size: summary.used, color: '#0052d9' },
    { key: 'free', label: '剩余空间', percent: freePercent, size: summary.free, color: '#f56c6c' }
  ]
})
const storageInfoSegments = computed(() => [...storageSegments.value].reverse())
const storageAriaLabel = computed(() => {
  const summary = storageSpaceSummary.value
  return summary.hasData ? `存储空间已使用${summary.usedPercent}%` : '暂无存储空间数据'
})
const storagePath = computed(() => diskSpace.value.path?.trim() || '')
const storageChartOption = computed<EChartsOption>(() => {
  const summary = storageSpaceSummary.value
  const hasData = summary.hasData
  const data = hasData
    ? storageSegments.value.map((segment) => ({
        name: segment.label,
        value: segment.size || 0,
        itemStyle: { color: segment.color }
      }))
    : [{ name: '暂无数据', value: 1, itemStyle: { color: '#eef0f5' } }]

  return {
    animation: hasData,
    tooltip: {
      show: hasData,
      trigger: 'item',
      formatter: '{b}: {d}%'
    },
    series: [
      {
        type: 'pie',
        radius: ['52%', '74%'],
        center: ['50%', '46%'],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: '#fff', borderWidth: 2 },
        label: {
          show: hasData,
          position: 'outside',
          formatter: (params) => `${params.name}\n${Number(params.percent).toFixed(2)}%`,
          color: 'inherit',
          width: 96,
          align: 'center',
          fontSize: 13,
          lineHeight: 20,
          overflow: 'truncate',
          ellipsis: '…',
          alignTo: 'labelLine',
          distanceToLabelLine: 2
        },
        labelLine: {
          show: hasData,
          length: 22,
          length2: 42,
          smooth: 0.15,
          lineStyle: { color: '#9aa4b2', width: 1 }
        },
        labelLayout: { hideOverlap: true, moveOverlap: 'shiftY' },
        emphasis: { scale: true, scaleSize: 4 },
        data
      }
    ]
  }
})

function firstStorageValue(source: DiskSpace, fields: string[]) {
  for (const field of fields) {
    const value = parseStorageValue(source[field])
    if (value !== undefined) return value
  }
  return undefined
}

function parseStorageValue(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : undefined
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return parseStorageValue(value.value)
  }
  if (typeof value !== 'string') return undefined
  const match = value.trim().replace(/,/g, '').match(/^(\d+(?:\.\d+)?)\s*(b|kb|mb|gb|tb|pb)?$/i)
  if (!match) return undefined
  const unit = (match[2] || 'b').toLowerCase()
  const multiplier: Record<string, number> = { b: 1, kb: 1024, mb: 1024 ** 2, gb: 1024 ** 3, tb: 1024 ** 4, pb: 1024 ** 5 }
  return Number(match[1]) * multiplier[unit]
}

function firstStoragePercentage(source: DiskSpace, fields: string[]) {
  for (const field of fields) {
    const rawValue = source[field]
    const value = typeof rawValue === 'string' ? Number(rawValue.trim().replace(/%$/, '')) : rawValue
    if (typeof value === 'number' && Number.isFinite(value)) return Math.min(Math.max(value > 1 ? value / 100 : value, 0), 1)
  }
  return undefined
}

function formatStorageSize(value: number | undefined) {
  if (value === undefined) return '--'
  if (value === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const unitIndex = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
  const amount = value / 1024 ** unitIndex
  return `${amount.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
}

onMounted(async () => {
  const [countsResult, entriesResult, optionsResult, diskSpaceResult] = await Promise.allSettled([
    getHomeCounts(),
    getQuickEntries(),
    getQuickEntryOptions(),
    getDiskSpace()
  ])
  if (countsResult.status === 'fulfilled' && countsResult.value) Object.assign(counts, countsResult.value)
  if (entriesResult.status === 'fulfilled') quickEntries.value = entriesResult.value || []
  if (optionsResult.status === 'fulfilled') {
    iconOptions.value = optionsResult.value.icons
    projectOptions.value = optionsResult.value.projects
  }
  if (diskSpaceResult.status === 'fulfilled' && diskSpaceResult.value?.length) diskSpace.value = diskSpaceResult.value[0]
  await loadRecentIvus()
})
async function loadRecentIvus() {
  try {
    recentIvus.value = await getRecentIvus({ name: searchValue.value })
  } catch {
    recentIvus.value = []
  }
}
function goTo(path: string) {
  void router.push(path)
}
function openEntry(path?: string) {
  const target = resolveQuickEntryTarget(path)
  if (!target) return
  if (/^https?:\/\//i.test(target)) window.open(target, '_blank', 'noopener,noreferrer')
  else goTo(target)
}
function openManual() {
  window.open('/manual/userManual.pdf', '_blank', 'noopener,noreferrer')
}
function openCustomize() {
  quickEntryDraft.value = createQuickEntryDraft(quickEntries.value)
  customizeVisible.value = true
}
function resetCustomize() {
  quickEntryDraft.value = createQuickEntryDraft(quickEntries.value)
}
async function saveCustomize() {
  if (customizeSaving.value) return
  let payload: QuickEntry[]
  try {
    payload = buildQuickEntryPayload(quickEntryDraft.value, projectOptions.value)
  } catch (error) {
    ElMessage.error(error instanceof QuickEntryValidationError ? error.message : '快捷菜单数据不正确')
    return
  }

  customizeSaving.value = true
  try {
    await updateQuickEntries(payload)
    quickEntries.value = await getQuickEntries()
    customizeVisible.value = false
    ElMessage.success('快捷菜单编辑成功！')
  } catch {
    ElMessage.error('快捷菜单编辑失败，请稍后重试')
  } finally {
    customizeSaving.value = false
  }
}
function openIvusDetail(row: RecentIvus) {
  void router.push({ path: '/patient/detail', query: { patientId: row.patientId, active: '3' } })
}
</script>

<style scoped>
.home {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding: 10px var(--page-gutter) 0;
}
.home-quick-entry {
  display: flex;
  align-items: flex-start;
  min-height: 86px;
}
.quick-entry,
.customize-entry {
  flex: 0 0 86px;
  width: 86px;
  height: 86px;
  margin-right: 10px;
  padding: 0;
  border: 0;
  color: #000;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  text-align: center;
}
.quick-entry {
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 0 18px rgba(0, 0, 0, 0.06);
}
.quick-entry img {
  display: block;
  width: 38px;
  height: 38px;
  margin: 10px auto 0px;
  object-fit: contain;
}
.quick-entry span,
.customize-entry span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.customize-entry .el-icon {
  display: flex;
  width: 100%;
  height: 51px;
  font-size: 17px;
}
.home-summary {
  display: grid;
  grid-template-columns: minmax(0, 31fr) repeat(3, minmax(0, 23fr));
  gap: 10px;
  margin-top: 10px;
}
.user-card,
.summary-card {
  position: relative;
  height: 126px;
  min-width: 0;
  overflow: hidden;
  border: 0;
  background: #fff;
  box-shadow: 0 0 18px rgba(0, 0, 0, 0.06);
  text-align: left;
}
.user-card {
  padding: 10px;
}
.summary-card {
  display: block;
  padding: 10px;
  color: #000;
  cursor: pointer;
}
.summary-background {
  position: absolute;
  top: 0;
  left: 0;
}
.user-title {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.user-title strong {
  overflow: hidden;
  font-size: 18px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-title button {
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  color: #0052d9;
  background: transparent;
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
}
.user-card p {
  position: relative;
  z-index: 1;
  margin-top: 5px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
}
.hospital-picture {
  margin-top: 5px;
  height: auto;
  width: auto;
  min-height: 40px;
  max-height: 42px;
  object-fit: contain;
}
.summary-card > span,
.summary-card > strong,
.summary-card > small {
  position: relative;
  z-index: 1;
  display: block;
}
.summary-card > span,
.summary-card > small {
  font-size: 12px;
}
.summary-card > small {
  color: rgba(0, 0, 0, 0.4);
}
.summary-card > strong {
  padding: 5px 0;
  color: #003c71;
  font-size: 40px;
  font-weight: 400;
  line-height: 48px;
}
.summary-card.ivus > strong {
  color: #f33;
}
.summary-card.clinic > strong {
  color: #008089;
}
.home-panel {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  margin-top: 10px;
  padding:5px 16px;
  background: #fff;
  box-shadow: 0 0 18px rgba(0, 0, 0, 0.06);
}
.recent-tabs {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}
.recent-tabs :deep(.el-tabs__content) {
  flex: 1 1 0;
  min-height: 0;
}
.recent-tabs :deep(.recent-tab-pane),
.recent-tabs :deep(.storage-tab-pane) {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
}
.panel-search {
  display: flex;
  align-items: center;
  min-height: 40px;
  font-size: 14px;
}
.home-search {
  width: 240px;
}
.storage-space-content {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  min-height: 0;
  align-items: center;
  justify-content: center;
  gap: 72px;
  overflow: auto;
  padding: 20px;
}
.storage-chart {
  flex: 0 1 500px;
  width: min(100%, 500px);
  min-width: min(300px, 100%);
  height: clamp(220px, 26vw, 280px);
}
.storage-space-info {
  flex: 0 1 280px;
  width: min(100%, 260px);
  min-width: min(220px, 100%);
}
.storage-summary-list {
  margin-bottom: 26px;
}
.storage-summary-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 18px;
}
.storage-summary-item:last-child {
  margin-bottom: 0;
}
.storage-summary-marker {
  flex: 0 0 10px;
  width: 10px;
  height: 10px;
  margin-top: 5px;
  border-radius: 50%;
}
.storage-summary-text {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.storage-summary-text strong {
  color: rgba(0, 0, 0, 0.72);
  font-size: 15px;
  font-weight: 500;
}
.storage-summary-text span {
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
}
.storage-total {
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
}
.storage-path {
  max-width: 280px;
  margin-top: 10px;
  overflow-wrap: anywhere;
  color: rgba(0, 0, 0, 0.4);
  font-size: 12px;
  line-height: 1.5;
}
.storage-empty {
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
}
@media (max-width: 768px) {
  .storage-space-content {
    align-content: flex-start;
    flex-direction: column;
    gap: 12px;
    justify-content: flex-start;
    padding: 12px;
  }
  .storage-chart {
    flex: 0 0 auto;
    width: 100%;
    min-width: 0;
    height: clamp(220px, 52vw, 280px);
  }
  .storage-space-info {
    flex: 0 0 auto;
    width: 100%;
    min-width: 0;
  }
  .storage-path {
    max-width: none;
  }
}
@media (min-resolution: 1.25dppx) and (min-width: 769px) {
  .storage-space-content {
    gap: 48px;
  }
  .storage-chart {
    flex-basis: 430px;
    width: min(100%, 430px);
    min-width: min(280px, 100%);
    height: clamp(200px, 22vw, 220px);
  }
  .storage-space-info {
    flex-basis: 280px;
    width: min(100%, 240px);
    min-width: min(210px, 100%);
  }
}
.table-area {
  flex: 1 1 auto;
  min-height: 0;
  margin-top: 20px;
}
.recent-table {
  width: 100%;
}
.recent-table :deep(th.el-table__cell) {
  color: rgba(0, 0, 0, 0.4);
  background: #fafafa;
  font-size: 14px;
  font-weight: 400;
}
.recent-table :deep(.el-table__inner-wrapper::before) {
  height: 0;
}
.empty-run {
  color: rgba(0, 0, 0, 0.4);
}
.panel-action {
  flex: 0 0 auto;
  padding-top: 12px;
  text-align: center;
}
.quick-editor-row {
  display: grid;
  grid-template-columns: 40px 100px 130px 130px;
  gap: 10px;
  margin-top: 20px;
}
.quick-editor-header {
  margin-top: 0;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}
.quick-order {
  height: 40px;
  border: 1px solid #dcdcdc;
  border-radius: 3px;
  line-height: 38px;
  text-align: center;
}
.quick-icon-select {
  width: 100px;
}
.selected-quick-icon,
.quick-icon-option {
  display: block;
  width: 24px;
  height: 24px;
  margin: auto;
  object-fit: contain;
}
:deep(.quick-entry-dialog .el-dialog__title) {
  font-weight: 700;
}
:deep(.quick-entry-dialog .el-dialog__body) {
  padding-bottom: 40px;
}
:deep(.quick-entry-dialog .el-dialog__footer) {
  padding: 12px 20px 0px;
  /* background: #fafafa; */
}
:deep(.quick-entry-dialog .el-dialog__close) {
  color: #000;
  font-size: 20px;
}
</style>
